import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Save, X, Power, KeyRound, RefreshCw, Copy, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const ROLE_LABELS = { owner: "Owner", admin: "Admin", chaplain: "Chaplain" };

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [lastResetUrl, setLastResetUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tr, mr] = await Promise.all([api.get("/admin/team"), api.get("/auth/me")]);
      setMembers(tr.data || []);
      setMe(mr.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const currentIsOwner = me?.role === "owner";

  return (
    <div data-testid="admin-team">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Team</h2>
          <p className="text-sm text-slate-500">Invite, manage, and de-activate CCCHAPS admins and chaplains.</p>
        </div>
        <button data-testid="team-invite-btn" onClick={() => { setLastInviteUrl(null); setInviteOpen(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"><Plus className="w-4 h-4" strokeWidth={2.2} /> Invite member</button>
      </div>

      {lastInviteUrl && <LinkBanner testId="team-invite-banner" title="Invitation created — email delivery is not configured" url={lastInviteUrl} onClose={() => setLastInviteUrl(null)} />}
      {lastResetUrl && <LinkBanner testId="team-reset-banner" title="Password reset link — email delivery is not configured" url={lastResetUrl} onClose={() => setLastResetUrl(null)} />}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-3">Name</th>
                  <th className="text-left px-3 py-3">Email</th>
                  <th className="text-left px-3 py-3">Role</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-left px-3 py-3">Last login</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((m) => (
                  <MemberRow key={m.id} m={m} me={me} onChange={load} onInviteUrl={setLastInviteUrl} onResetUrl={setLastResetUrl} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} currentIsOwner={currentIsOwner} onDone={(url) => { setInviteOpen(false); load(); if (url) setLastInviteUrl(url); }} />}
    </div>
  );
}

function LinkBanner({ testId, title, url, onClose }) {
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed — copy manually"); } };
  return (
    <div data-testid={testId} className="mb-4 rounded-xl border border-amber-300 bg-amber-50/70 p-4 flex items-start gap-3">
      <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" strokeWidth={2.2} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-amber-900 font-medium">{title}</p>
        <p className="text-xs text-amber-800 mt-0.5">Send this one-time link to the user through a secure channel. Expires in 48 hours.</p>
        <code className="mt-2 block text-xs bg-white/60 text-slate-800 px-2 py-1.5 rounded border border-amber-200 break-all">{url}</code>
        <div className="mt-2 flex gap-2">
          <button onClick={copy} data-testid={`${testId}-copy`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-amber-300 bg-white text-xs text-amber-900"><Copy className="w-3.5 h-3.5" /> Copy link</button>
          <button onClick={onClose} className="text-xs text-amber-800 hover:text-amber-900">Dismiss</button>
        </div>
      </div>
    </div>
  );
}

function MemberRow({ m, me, onChange, onInviteUrl, onResetUrl }) {
  const [busy, setBusy] = useState(false);
  const meIsOwner = me?.role === "owner";
  const isSelf = m.is_current_user;
  const canManageThis = meIsOwner || (m.role !== "owner");

  const patch = async (updates) => {
    setBusy(true);
    try { await api.patch(`/admin/team/${m.id}`, updates); toast.success("Saved"); onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const triggerReset = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/team/${m.id}/trigger-reset`); toast.success(r.data.email_sent ? "Reset email sent" : "Reset link generated"); if (r.data.reset_url) onResetUrl(r.data.reset_url); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/team/${m.id}/regenerate-invite`); toast.success(r.data.email_sent ? "Invite email sent" : "New invite link generated"); if (r.data.invitation_url) onInviteUrl(r.data.invitation_url); onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const roleBadge = m.role === "owner" ? "bg-[#4A6FA5]/10 text-[#3B597B]" : m.role === "admin" ? "bg-slate-100 text-slate-700" : "bg-emerald-50 text-emerald-700";
  const statusBadge = m.status === "active" ? "bg-emerald-50 text-emerald-700" : m.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500";

  return (
    <tr data-testid={`team-row-${m.id}`} className="hover:bg-slate-50/60">
      <td className="px-3 py-3 text-slate-900 font-medium">{m.name}{isSelf && <span className="ml-1 text-[10px] text-slate-400">(you)</span>}</td>
      <td className="px-3 py-3 text-slate-600">{m.email}</td>
      <td className="px-3 py-3">
        <select value={m.role} disabled={busy || !canManageThis} onChange={(e) => patch({ role: e.target.value })} data-testid={`team-role-${m.id}`} className="text-xs rounded border border-slate-300 px-1.5 py-0.5 bg-white disabled:opacity-60">
          {(meIsOwner ? ["owner","admin","chaplain"] : ["admin","chaplain"]).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
      </td>
      <td className="px-3 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusBadge}`}>{m.status}</span></td>
      <td className="px-3 py-3 text-xs text-slate-500">{m.last_login_at ? new Date(m.last_login_at).toLocaleString() : "—"}</td>
      <td className="px-3 py-3 text-right space-x-1 whitespace-nowrap">
        {m.status === "pending" && (
          <button onClick={regenerate} disabled={busy || !canManageThis} data-testid={`team-regen-${m.id}`} className="inline-flex items-center gap-1 text-xs text-[#4A6FA5] hover:text-[#3B597B] px-2 py-1"><RefreshCw className="w-3.5 h-3.5" /> New invite</button>
        )}
        {m.status === "active" && !isSelf && (
          <button onClick={triggerReset} disabled={busy || !canManageThis} data-testid={`team-reset-${m.id}`} className="inline-flex items-center gap-1 text-xs text-[#4A6FA5] hover:text-[#3B597B] px-2 py-1"><KeyRound className="w-3.5 h-3.5" /> Reset password</button>
        )}
        {m.status !== "inactive" && !isSelf && (
          <button onClick={() => patch({ status: "inactive" })} disabled={busy || !canManageThis} data-testid={`team-deactivate-${m.id}`} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 px-2 py-1"><Power className="w-3.5 h-3.5" /> Deactivate</button>
        )}
        {m.status === "inactive" && (
          <button onClick={() => patch({ status: "active" })} disabled={busy || !canManageThis} data-testid={`team-reactivate-${m.id}`} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 px-2 py-1"><Power className="w-3.5 h-3.5" /> Reactivate</button>
        )}
      </td>
    </tr>
  );
}

function InviteModal({ onClose, currentIsOwner, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("chaplain");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim()) { toast.error("Name and email required."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/team/invite", { name: name.trim(), email: email.trim().toLowerCase(), role });
      toast.success(r.data.email_sent ? "Invitation sent by email" : "Invitation link generated");
      onDone(r.data.invitation_url || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="team-invite-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-lg max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg text-slate-900">Invite team member</h3>
          <button type="button" onClick={onClose} data-testid="team-invite-close" className="text-slate-400 hover:text-slate-800"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Full name</span><input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-invite-name" className="mt-1 w-full px-3 py-2 rounded border border-slate-300 text-sm" /></label>
          <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Email</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email" className="mt-1 w-full px-3 py-2 rounded border border-slate-300 text-sm" /></label>
          <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="team-invite-role" className="mt-1 w-full px-3 py-2 rounded border border-slate-300 text-sm bg-white">
              <option value="chaplain">Chaplain</option>
              <option value="admin">Admin</option>
              {currentIsOwner && <option value="owner">Owner</option>}
            </select>
          </label>
        </div>
        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500"><ShieldCheck className="w-3.5 h-3.5 mt-0.5" /><p>Chaplains see confidential support requests and activity reports but cannot manage organizations, CALLACHAP routing, or team members.</p></div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="team-invite-submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider"><Save className="w-4 h-4" /> {submitting ? "Sending…" : "Send invitation"}</button>
        </div>
      </form>
    </div>
  );
}
